import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../services/api-client.js';
import { formatCurrency } from '@cardealeros/shared';

const TIER_COLORS: Record<string, string> = {
  free: 'border-surface-600',
  starter: 'border-green-600',
  pro: 'border-brand-600',
  enterprise: 'border-purple-600',
};

const TIER_BADGES: Record<string, string> = {
  free: 'bg-surface-700 text-surface-400',
  starter: 'bg-green-900/50 text-green-400',
  pro: 'bg-brand-900/50 text-brand-400',
  enterprise: 'bg-purple-900/50 text-purple-400',
};

export default function BillingPage() {
  const { data: statusRes, isLoading: statusLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => api.get<any>('/billing/status'),
  });

  const { data: tiersRes } = useQuery({
    queryKey: ['billing-tiers'],
    queryFn: () => api.get<any>('/billing/tiers'),
  });

  const checkoutMutation = useMutation({
    mutationFn: (tier: string) => api.post<any>('/billing/checkout', { tier }),
    onSuccess: (data) => {
      if (data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      }
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => api.post<any>('/billing/portal'),
    onSuccess: (data) => {
      if (data.data?.portal_url) {
        window.location.href = data.data.portal_url;
      }
    },
  });

  const billing = statusRes?.data;
  const tiers = tiersRes?.data;

  if (statusLoading) return <div className="page text-center py-20 text-surface-500">Loading...</div>;

  return (
    <div className="page">
      <h2 className="text-2xl font-bold mb-1">Billing & Plans</h2>
      <p className="text-surface-500 text-sm mb-6">Manage your subscription</p>

      {/* Current Plan Status */}
      {billing && (
        <div className="card p-4 mb-6 border-brand-700">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-xs text-surface-500">Current Plan</p>
              <p className="text-lg font-bold capitalize">{billing.current_tier}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              billing.subscription_status === 'active' ? 'bg-green-900/50 text-green-400' :
              billing.subscription_status === 'past_due' ? 'bg-yellow-900/50 text-yellow-400' :
              billing.subscription_status === 'cancelled' ? 'bg-red-900/50 text-red-400' :
              'bg-surface-700 text-surface-400'
            }`}>{billing.subscription_status}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-surface-500 text-xs">Vehicles</p>
              <p className="font-medium">
                {billing.vehicle_count} / {billing.vehicle_limit === -1 ? 'Unlimited' : billing.vehicle_limit}
              </p>
            </div>
            {billing.at_limit && (
              <div className="col-span-2 bg-yellow-900/20 rounded-lg p-2">
                <p className="text-yellow-400 text-xs font-medium">Vehicle limit reached — upgrade to add more</p>
              </div>
            )}
          </div>
          {billing.stripe_customer_id && (
            <button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="btn-secondary w-full mt-4 text-sm"
            >
              {portalMutation.isPending ? 'Opening...' : 'Manage Billing'}
            </button>
          )}
        </div>
      )}

      {/* Available Plans */}
      {tiers && (
        <div className="space-y-3">
          <h3 className="font-semibold text-sm text-surface-400">Available Plans</h3>
          {Object.entries(tiers).map(([key, tier]: [string, any]) => {
            const isCurrent = billing?.current_tier === key;
            return (
              <div
                key={key}
                className={`card p-4 ${isCurrent ? 'border-brand-500' : TIER_COLORS[key] ?? ''}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold">{tier.name}</h4>
                      {isCurrent && (
                        <span className="px-2 py-0.5 rounded text-xs bg-brand-600 text-white">Current</span>
                      )}
                    </div>
                    <p className="text-lg font-bold text-brand-400 mt-1">
                      {tier.price_cents === 0 ? 'Free' : `${formatCurrency(tier.price_cents)}/mo`}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${TIER_BADGES[key] ?? ''}`}>
                    {tier.vehicles === -1 ? 'Unlimited' : `${tier.vehicles} vehicles`}
                  </span>
                </div>
                <ul className="space-y-1 mb-4">
                  {tier.features.map((f: string, i: number) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-surface-400">
                      <span className="text-green-400 text-xs">+</span>
                      {f}
                    </li>
                  ))}
                </ul>
                {!isCurrent && key !== 'free' && (
                  <button
                    onClick={() => checkoutMutation.mutate(key)}
                    disabled={checkoutMutation.isPending}
                    className="btn-primary w-full text-sm"
                  >
                    {checkoutMutation.isPending ? 'Processing...' : `Upgrade to ${tier.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
